import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './PlayoffsPage.css';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { gameDayToDate } from '../utils/dateUtils';
import BoxScore from '../components/BoxScore';

interface Team {
  id: string;
  name: string;
  city: string;
  abbreviation: string;
  conference: string;
  division: string;
}

interface PlayoffGame {
  id: string;
  game_id?: string;
  series_id: string;
  home_team_id: string;
  away_team_id: string;
  home_score: number | null;
  away_score: number | null;
  winner: string | null;
  game_day: number;
  game_number: number;
  completed: boolean;
}

interface SeriesDto {
  id: string;
  round: string;
  conference: string;
  series_name: string;
  home_team_id: string | null;
  away_team_id: string | null;
  home_wins: number;
  away_wins: number;
  winner: string | null;
  status: string;
  start_day: number;
}

interface SeriesStanding {
  series_id: string;
  team1_id: string;
  team2_id: string;
  team1_wins: number;
  team2_wins: number;
  team1_name: string;
  team2_name: string;
  playoff_round: string;
  status: 'in_progress' | 'complete' | 'upcoming';
  winner_id: string | null;
  winner_name: string | null;
  series_name: string;
}

const PlayoffsPage: React.FC = () => {
  const { leagueId } = useParams();
  const [teams, setTeams] = useState<Map<string, Team>>(new Map());
  const [playoffGames, setPlayoffGames] = useState<PlayoffGame[]>([]);
  const [seriesStandings, setSeriesStandings] = useState<SeriesStanding[]>([]);
  const [league, setLeague] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<'bracket' | 'schedule'>('bracket');
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);

  // Format playoff game date: "G1 (Oct 1)" or "G3 (Oct 4)"
  const formatPlayoffDate = (gameDay: number, gameNumber: number, seasonYear: number): string => {
    const actualSeasonDay = gameDay + 183; // Playoff Day 1 = Oct 1 (Day 184)
    const date = gameDayToDate(actualSeasonDay, seasonYear);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `G${gameNumber} (${months[date.getMonth()]} ${date.getDate()})`;
  };

  useEffect(() => {
    loadPlayoffData();
  }, [leagueId]);

  async function loadPlayoffData() {
    setLoading(true);
    
    if (!isSupabaseConfigured() || !supabase) {
      setLoading(false);
      return;
    }

    // Load league
    const { data: leagueData } = await supabase
      .from('leagues')
      .select('*')
      .eq('id', leagueId)
      .single();
    
    if (leagueData) setLeague(leagueData);

    const seasonYear = leagueData?.season_year || 1902;

    // Load teams
    const { data: teamData } = await supabase
      .from('teams')
      .select('id, name, city, abbreviation, conference, division');
    
    if (teamData) {
      const map = new Map<string, Team>();
      (teamData as Team[]).forEach(t => map.set(t.id, t));
      setTeams(map);
    }

    // Load bracket
    const { data: bracketData } = await supabase
      .from('playoff_brackets')
      .select('*')
      .eq('league_id', leagueId)
      .eq('season_year', seasonYear)
      .limit(1);
    
    const bracket = bracketData && bracketData.length > 0 ? bracketData[0] : null;
    if (!bracket) {
      setLoading(false);
      return;
    }

    // Load series
    const { data: seriesData } = await supabase
      .from('playoff_series')
      .select('*')
      .eq('bracket_id', bracket.id);
    
    if (!seriesData || seriesData.length === 0) {
      setLoading(false);
      return;
    }

    const seriesList = seriesData as SeriesDto[];

    // Load ALL playoff games directly from league_schedule by league_id
    // (engine writes here with is_playoff=true; series_id matches playoff_series.id)
    const { data: gameData } = await supabase
      .from('league_schedule')
      .select('id, game_id, series_id, home_team_id, away_team_id, home_score, away_score, winner, game_day, game_number, status, is_playoff, playoff_round')
      .eq('league_id', leagueId)
      .eq('is_playoff', true)
      .order('game_day', { ascending: true })
      .order('game_number', { ascending: true });

    // Normalize league_schedule rows to PlayoffGame shape
    const normalizedGames: PlayoffGame[] = (gameData || []).map((g: any) => ({
      id: g.id,
      game_id: g.game_id,
      series_id: g.series_id,
      home_team_id: g.home_team_id,
      away_team_id: g.away_team_id,
      home_score: g.home_score,
      away_score: g.away_score,
      winner: g.winner,
      game_day: g.game_day,
      game_number: g.game_number,
      completed: g.status === 'completed',
    }));

    setPlayoffGames(normalizedGames);
    calculateSeriesStandings(seriesList, normalizedGames, teamData as Team[]);
    
    setLoading(false);
  }

  function calculateSeriesStandings(seriesList: SeriesDto[], games: PlayoffGame[], teamList: Team[]) {
    const teamMap = new Map(teamList?.map(t => [t.id, t]) || []);
    const standings: SeriesStanding[] = [];

    for (const series of seriesList) {
      // Use authoritative win counts from playoff_series table
      // (engine updates these when series complete)
      let team1Wins = series.home_wins || 0;
      let team2Wins = series.away_wins || 0;

      // If series still active, count from completed games for live accuracy
      if (series.status !== 'completed') {
        const seriesGames = games.filter(g => g.series_id === series.id);
        team1Wins = 0;
        team2Wins = 0;
        for (const game of seriesGames) {
          if (game.completed && game.winner) {
            if (game.winner === series.home_team_id) team1Wins++;
            else if (game.winner === series.away_team_id) team2Wins++;
          }
        }
      }

      const t1Id = series.home_team_id;
      const t2Id = series.away_team_id;
      let seriesStatus: 'in_progress' | 'complete' | 'upcoming' = 'upcoming';
      let winnerId: string | null = null;
      let winnerName: string | null = null;

      if (team1Wins >= 4 || team2Wins >= 4) {
        seriesStatus = 'complete';
        winnerId = team1Wins >= 4 ? t1Id : t2Id;
        winnerName = teamMap.get(winnerId || '')?.name || 'Unknown';
      } else if (team1Wins > 0 || team2Wins > 0) {
        seriesStatus = 'in_progress';
      }

      standings.push({
        series_id: series.id,
        team1_id: t1Id || '',
        team2_id: t2Id || '',
        team1_wins: team1Wins,
        team2_wins: team2Wins,
        team1_name: teamMap.get(t1Id || '')?.name || 'TBD',
        team2_name: teamMap.get(t2Id || '')?.name || 'TBD',
        playoff_round: series.round,
        status: seriesStatus,
        winner_id: winnerId,
        winner_name: winnerName,
        series_name: series.series_name || series.id.replace(/_/g, ' ').toUpperCase()
      });
    }

    setSeriesStandings(standings);
  }

  function getRoundLabel(round: string): string {
    switch (round) {
      case 'DS': return 'Division Series';
      case 'CS': return 'Conference Series';
      case 'WS': return 'World Series';
      default: return round;
    }
  }

  function getRoundOrder(round: string): number {
    switch (round) {
      case 'DS': return 1;
      case 'CS': return 2;
      case 'WS': return 3;
      default: return 0;
    }
  }

  // Sort series by status: in_progress > complete > upcoming
  function sortSeriesByStatus(a: SeriesStanding, b: SeriesStanding): number {
    const statusOrder: Record<string, number> = { 'in_progress': 0, 'complete': 1, 'upcoming': 2 };
    return (statusOrder[a.status] ?? 2) - (statusOrder[b.status] ?? 2);
  }

  const groupedByRound = seriesStandings.reduce((acc, series) => {
    const round = series.playoff_round;
    if (!acc[round]) acc[round] = [];
    acc[round].push(series);
    return acc;
  }, {} as Record<string, SeriesStanding[]>);

  // Sort series within each round by status
  Object.keys(groupedByRound).forEach(round => {
    groupedByRound[round].sort(sortSeriesByStatus);
  });

  if (loading) {
    return <div className="playoffs-loading">Loading playoffs...</div>;
  }

  if (seriesStandings.length === 0) {
    return (
      <div className="playoffs-empty">
        <h2>Playoffs</h2>
        <p>No playoff bracket generated yet. Playoffs begin after the regular season ends on September 30th.</p>
      </div>
    );
  }

  return (
    <div className="playoffs-page">
      <div className="playoffs-header">
        <h1>⚾ {league?.name || 'League'} Playoffs</h1>
        <div className="playoffs-tabs">
          <button 
            className={`tab-btn ${activeView === 'bracket' ? 'active' : ''}`}
            onClick={() => setActiveView('bracket')}
          >
            Bracket
          </button>
          <button 
            className={`tab-btn ${activeView === 'schedule' ? 'active' : ''}`}
            onClick={() => setActiveView('schedule')}
          >
            Schedule
          </button>
        </div>
      </div>

      {league?.status === 'off_season' && league?.champion_name && (
        <div className="champion-banner" style={{
          textAlign: 'center', padding: '16px', marginBottom: '20px',
          background: 'linear-gradient(135deg, rgba(212,160,23,0.15), rgba(232,184,42,0.05))',
          border: '1px solid #d4a017', borderRadius: '12px'
        }}>
          <div style={{ fontSize: '11px', letterSpacing: '3px', color: '#d4a017', textTransform: 'uppercase' }}>
            {league.status === 'off_season' ? 'Season Complete' : 'Playoffs'}
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#e8b82a', marginTop: '4px' }}>
            🏆 {league.champion_name}
          </div>
          <div style={{ fontSize: '13px', color: '#889', marginTop: '4px' }}>
            World Series Champion — Season {league.season_year || 1902}
          </div>
        </div>
      )}

      {activeView === 'bracket' && (
        <div className="playoffs-bracket">
          {Object.entries(groupedByRound)
            .sort((a, b) => getRoundOrder(a[0]) - getRoundOrder(b[0]))
            .map(([round, seriesList]) => (
              <div key={round} className="bracket-round">
                <h2 className="round-title">{getRoundLabel(round)}</h2>
                <div className="bracket-series-grid">
                  {seriesList.map(series => (
                    <div key={series.series_id} className={`series-card ${series.status}`}>
                      <div className="series-header">
                        <span className="series-id">{series.series_name}</span>
                        {series.status === 'complete' && (
                          <span className="series-winner-badge">✓ {series.winner_name}</span>
                        )}
                      </div>
                      <div className="series-matchup">
                        <div className={`team-row ${series.team1_wins >= 4 ? 'winner' : ''}`}>
                          <span className="team-name">{series.team1_name}</span>
                          <span className="team-wins">{series.team1_wins}</span>
                        </div>
                        <div className="vs-divider">vs</div>
                        <div className={`team-row ${series.team2_wins >= 4 ? 'winner' : ''}`}>
                          <span className="team-name">{series.team2_name}</span>
                          <span className="team-wins">{series.team2_wins}</span>
                        </div>
                      </div>
                      <div className="series-status">
                        {series.status === 'complete' 
                          ? `${series.winner_name} wins 4-${Math.min(series.team1_wins, series.team2_wins)}`
                          : series.status === 'in_progress'
                          ? `Series in progress`
                          : 'Series upcoming'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      {activeView === 'schedule' && (
        <div className="playoffs-schedule">
          {Object.entries(groupedByRound)
            .sort((a, b) => getRoundOrder(a[0]) - getRoundOrder(b[0]))
            .map(([round, seriesList]) => (
              <div key={round} className="schedule-round">
                <h2 className="round-title">{getRoundLabel(round)}</h2>
                {seriesList.map(series => {
                  const seriesGames = playoffGames
                    .filter(g => g.series_id === series.series_id)
                    .sort((a, b) => a.game_number - b.game_number);
                  
                  return (
                    <div key={series.series_id} className="schedule-series">
                      <div className="schedule-series-header">
                        <span className="series-label">{series.series_name}</span>
                        <span className="series-score">
                          {series.team1_name} {series.team1_wins} - {series.team2_wins} {series.team2_name}
                        </span>
                      </div>
                      <div className="schedule-games">
                        {seriesGames.map(game => (
                          <div 
                            key={game.id} 
                            className={`schedule-game ${game.completed ? 'completed clickable' : ''}`}
                            onClick={() => game.completed && setSelectedGameId(game.game_id ?? game.id)}
                          >
                            <div className="game-number">G{game.game_number}</div>
                            <div className="game-teams">
                              <div className="game-team">
                                <span className="team-abbr">{teams.get(game.away_team_id)?.abbreviation || '???'}</span>
                                <span className="team-score">{game.away_score ?? '-'}</span>
                              </div>
                              <div className="game-at">@</div>
                              <div className="game-team">
                                <span className="team-abbr">{teams.get(game.home_team_id)?.abbreviation || '???'}</span>
                                <span className="team-score">{game.home_score ?? '-'}</span>
                              </div>
                            </div>
                            <div className="game-status">
                              {game.completed 
                                ? 'Final'
                                : formatPlayoffDate(game.game_day, game.game_number, league?.season_year || new Date().getFullYear())}
                            </div>
                            {game.completed && game.winner && (
                              <div className="game-winner">
                                {teams.get(game.winner)?.abbreviation || '???'} W
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      )}

      {/* BoxScore Modal */}
      {selectedGameId && (
        <BoxScore gameId={selectedGameId} onClose={() => setSelectedGameId(null)} />
      )}
    </div>
  );
};

export default PlayoffsPage;
